'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import styles from '../styles/TosAcceptance.module.css';

// Version for forcing re-acceptance when TOS changes
const TOS_VERSION = '2026-02-05';
const STORAGE_KEY = 'altermundi_meet_tos_accepted';

interface TosStorageData {
  accepted: boolean;
  version: string;
  timestamp: string;
}

/**
 * Hook to check and manage TOS acceptance state
 */
export function useTosAcceptance() {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: TosStorageData = JSON.parse(stored);
        // Only consider accepted if version matches
        if (data.accepted && data.version === TOS_VERSION) {
          setAccepted(true);
        }
      }
    } catch {
      // localStorage not available or corrupted data
    }
    setLoading(false);
  }, []);

  const accept = useCallback(() => {
    const data: TosStorageData = {
      accepted: true,
      version: TOS_VERSION,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setAccepted(true);
    } catch {
      // localStorage not available
      setAccepted(true); // Still allow in session
    }
  }, []);

  const revoke = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage not available
    }
    setAccepted(false);
  }, []);

  return { accepted, loading, accept, revoke };
}

interface TosCheckboxProps {
  onAcceptChange?: (accepted: boolean) => void;
  disabled?: boolean;
}

/**
 * Checkbox component for accepting TOS with bilingual text
 */
export function TosCheckbox({ onAcceptChange, disabled = false }: TosCheckboxProps) {
  const { accepted, loading, accept, revoke } = useTosAcceptance();

  useEffect(() => {
    if (!loading && onAcceptChange) {
      onAcceptChange(accepted);
    }
  }, [accepted, loading, onAcceptChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      accept();
    } else {
      revoke();
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className={styles.tosContainer}>
      <label className={`${styles.tosLabel} ${disabled ? styles.disabled : ''}`}>
        <span className={styles.checkboxWrapper}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={accepted}
            onChange={handleChange}
            disabled={disabled}
          />
          <span className={styles.checkboxCustom}>
            <span className={styles.checkmark}>
              <svg viewBox="0 0 12 12" fill="none" stroke="#000" strokeWidth="2">
                <path d="M2 6l3 3 5-5" />
              </svg>
            </span>
          </span>
        </span>
        <span className={styles.tosText}>
          <span className={styles.tosTextPrimary}>
            Al continuar, acepto los{' '}
            <Link href="/legal" target="_blank">
              Términos de Servicio
            </Link>
            , incluyendo el consentimiento para grabaciones y la exoneración de responsabilidad.
          </span>
          <span className={styles.tosTextSecondary}>
            By continuing, I accept the{' '}
            <Link href="/legal" target="_blank">
              Terms of Service
            </Link>
            , including recording consent and liability disclaimer.
          </span>
        </span>
      </label>
    </div>
  );
}

interface TosGateProps {
  children: React.ReactNode;
}

/**
 * Gate component that blocks content until TOS is accepted
 */
export function TosGate({ children }: TosGateProps) {
  const { accepted, loading } = useTosAcceptance();
  const [localAccepted, setLocalAccepted] = useState(false);

  useEffect(() => {
    setLocalAccepted(accepted);
  }, [accepted]);

  if (loading) {
    return (
      <div className={styles.tosGate}>
        <div className={styles.tosGateCard}>
          <p style={{ color: 'rgba(255,255,255,0.7)' }}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (!localAccepted) {
    return (
      <div className={styles.tosGate}>
        <div className={styles.tosGateCard}>
          <h1 className={styles.tosGateTitle}>Términos de Servicio</h1>
          <p className={styles.tosGateSubtitle}>
            Antes de continuar, por favor acepta nuestros términos
          </p>

          <div className={styles.tosGateCheckbox}>
            <TosCheckbox onAcceptChange={setLocalAccepted} />
          </div>

          <button
            className={styles.tosGateContinue}
            disabled={!localAccepted}
            onClick={() => {
              // Force re-check from localStorage
              setLocalAccepted(true);
            }}
          >
            Continuar / Continue
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
